import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { ToastService } from 'angular-toastify';
import {
  EV_CONNECTOR_CODE_OPTIONS,
  EvConnectorTypeRow,
} from '../../../../../core/models/system-settings/ev-connector-type.model';
import { MapperEvConnectorTypeService } from '../../../../../core/mappers/system-settings/mapper-ev-connector-type.service';
import { ApiEvConnectorTypeService } from '../../../../../core/services/api/system-settings/api-ev-connector-type.service';
import { MenuPermissionService } from '../../../../../core/services/menu-permission.service';
import { ModalAdminActivityHistoryComponent } from '../modal-admin-activity-history/modal-admin-activity-history.component';
import { AdminActivityEntityTypes } from '../../../../../core/models/system-settings/admin-activity-log.model';

@Component({
  selector: 'app-modal-ev-connector-type',
  standalone: true,
  imports: [CommonModule, FormsModule, ModalAdminActivityHistoryComponent],
  templateUrl: './modal-ev-connector-type.component.html',
  styleUrl: './modal-ev-connector-type.component.css',
})
export class ModalEvConnectorTypeComponent implements OnInit {
  @Input() itemId = 0;
  @Output() close = new EventEmitter<void>();
  @Output() saveSuccess = new EventEmitter<void>();

  readonly activityEntityTypes = AdminActivityEntityTypes;
  showActivityHistory = false;
  readonly codeOptions = EV_CONNECTOR_CODE_OPTIONS;
  formData!: EvConnectorTypeRow;
  isLoading = signal(false);
  isSaving = signal(false);

  private readonly api = inject(ApiEvConnectorTypeService);
  private readonly mapper = inject(MapperEvConnectorTypeService);
  private readonly toast = inject(ToastService);
  private readonly cookieService = inject(CookieService);
  private readonly router = inject(Router);
  private readonly permissionService = inject(MenuPermissionService);

  get isEdit(): boolean {
    return this.itemId > 0;
  }

  ngOnInit(): void {
    this.formData = this.mapper.createEmpty();
    if (this.isEdit) this.loadDetail();
  }

  onClose(): void {
    this.close.emit();
  }

  onCodeChange(code: string): void {
    this.formData.code = code;
    if (this.isEdit || this.formData.name.trim()) return;
    if (code === 'ac') {
      this.formData.name = 'AC (ช้า)';
      this.formData.nameEn = 'AC Slow';
    } else if (code === 'dc') {
      this.formData.name = 'DC (เร็ว)';
      this.formData.nameEn = 'DC Fast';
    } else if (code === 'ac_dc') {
      this.formData.name = 'AC + DC';
      this.formData.nameEn = 'AC/DC Combo';
    }
  }

  onSave(): void {
    if (!this.formData.code?.trim()) {
      this.toast.error('กรุณาเลือกรหัส');
      return;
    }
    if (!this.formData.name?.trim()) {
      this.toast.error('กรุณาระบุชื่อ');
      return;
    }
    if (this.isEdit && !this.permissionService.canUpdate(this.router.url)) {
      this.toast.error('ไม่มีสิทธิ์แก้ไข');
      return;
    }
    if (!this.isEdit && !this.permissionService.canCreate(this.router.url)) {
      this.toast.error('ไม่มีสิทธิ์สร้าง');
      return;
    }

    const userId = this.resolveUserId();
    const menuId = this.permissionService.getMenuId(this.router.url) ?? 0;
    if (!userId || !menuId) {
      this.toast.error('ไม่พบข้อมูลผู้ใช้หรือสิทธิ์เมนู');
      return;
    }

    const request$ = this.isEdit
      ? this.api.updateEvConnectorType(this.mapper.mapUpdatePayload(this.formData, userId, menuId))
      : this.api.insertEvConnectorType(this.mapper.mapInsertPayload(this.formData, userId, menuId));

    this.isSaving.set(true);
    request$.subscribe({
      next: (res) => {
        this.isSaving.set(false);
        const mapped = this.mapper.mapActionResponse(res);
        if (mapped.isSuccess) {
          this.toast.success(mapped.message || 'บันทึกเรียบร้อย');
          this.saveSuccess.emit();
        } else {
          this.toast.error(mapped.message || 'บันทึกไม่สำเร็จ');
        }
      },
      error: (err) => {
        this.isSaving.set(false);
        this.toast.error(err?.error?.message || 'เกิดข้อผิดพลาด');
      },
    });
  }

  private loadDetail(): void {
    this.isLoading.set(true);
    this.api.getEvConnectorTypeById(this.itemId).subscribe({
      next: (res) => {
        this.formData = this.mapper.mapRow(res);
        this.isLoading.set(false);
      },
      error: (err) => {
        this.isLoading.set(false);
        this.toast.error(err?.error?.message || 'โหลดรายละเอียดไม่สำเร็จ');
        this.onClose();
      },
    });
  }

  private resolveUserId(): number {
    return Number(localStorage.getItem('userId')) || Number(this.cookieService.get('user_id')) || 0;
  }

  get canShowActivityHistory(): boolean {
    return this.itemId > 0;
  }

  openActivityHistory(): void {
    if (!this.canShowActivityHistory) return;
    this.showActivityHistory = true;
  }

  closeActivityHistory(): void {
    this.showActivityHistory = false;
  }
}
